import { useCallback, useEffect, useRef, useState } from 'react';
import { getSection, type Sections } from '../../../services/intelligenceReport.service';
import { sectionsForView, type Criteria, type Section } from './criteria';
export interface SectionState<T> { loading: boolean; error: string | null; data: T | null }
export type ReportState = { [K in Section]?: SectionState<Sections[K]> };
export function useIntelligenceReport(criteria: Criteria | null, runId = 0) {
  const [state, setState] = useState<ReportState>({});
  const generation = useRef(0);
  const controllers = useRef<Partial<Record<Section, AbortController>>>({});
  const load = useCallback((section: Section, c: Criteria, version: number) => {
    controllers.current[section]?.abort();
    const controller = new AbortController();
    controllers.current[section] = controller;
    setState(s => ({ ...s, [section]: { loading: true, error: null, data: null } }));
    getSection(section, c, controller.signal).then(data => {
      if (!controller.signal.aborted && version === generation.current) setState(s => ({ ...s, [section]: { loading: false, error: null, data } }));
    }).catch(() => {
      if (!controller.signal.aborted && version === generation.current) setState(s => ({ ...s, [section]: { loading: false, error: 'Unable to load this section. Please retry.', data: null } }));
    });
  }, []);
  useEffect(() => {
    const version = ++generation.current;
    setState({});
    if (criteria) for (const section of sectionsForView(criteria.view)) load(section, criteria, version);
    const active = controllers.current;
    return () => { Object.values(active).forEach(c => c?.abort()); };
  }, [criteria, runId, load]);
  const retry = useCallback((section: Section) => { if (criteria) load(section, criteria, generation.current); }, [criteria, load]);
  return { state, retry };
}
